"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultFilterBar = exports.useFilterBarProps = void 0;
var tslib_1 = require("tslib");
// (C) 2021-2022 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var sdk_model_1 = require("@gooddata/sdk-model");
var model_1 = require("../../../model");
var dashboardContexts_1 = require("../../dashboardContexts");
var dragAndDrop_1 = require("../../dragAndDrop");
var dateFilter_1 = require("../dateFilter");
var DefaultFilterBarContainer_1 = require("./DefaultFilterBarContainer");
var useFiltersWithAddedPlaceholder_1 = require("./useFiltersWithAddedPlaceholder");
var HiddenFilterBar_1 = require("./HiddenFilterBar");
var legacyFilterConvertors_1 = require("../../../_staging/dashboard/legacyFilterConvertors");
/**
 * @alpha
 */
exports.useFilterBarProps = function () {
    var filters = model_1.useDashboardSelector(model_1.selectFilterContextFilters);
    var supportElementUris = model_1.useDashboardSelector(model_1.selectSupportsElementUris);
    var dispatch = model_1.useDashboardDispatch();
    var onAttributeFilterChanged = react_1.useCallback(function (filter) {
        var convertedFilter = supportElementUris
            ? filter
            : legacyFilterConvertors_1.convertDashboardAttributeFilterElementsValuesToUris(filter);
        var _a = convertedFilter.attributeFilter, attributeElements = _a.attributeElements, negativeSelection = _a.negativeSelection, localIdentifier = _a.localIdentifier;
        dispatch(model_1.changeAttributeFilterSelection(localIdentifier, attributeElements, negativeSelection ? "NOT_IN" : "IN"));
    }, [dispatch, supportElementUris]);
    var onDateFilterChanged = react_1.useCallback(function (filter, dateFilterOptionLocalId) {
        if (!filter) {
            // all time filter
            dispatch(model_1.clearDateFilterSelection());
        }
        else {
            var _a = filter.dateFilter, type = _a.type, granularity = _a.granularity, from = _a.from, to = _a.to;
            dispatch(model_1.changeDateFilterSelection(type, granularity, from, to, dateFilterOptionLocalId));
        }
    }, [dispatch]);
    return { filters: filters, onAttributeFilterChanged: onAttributeFilterChanged, onDateFilterChanged: onDateFilterChanged, DefaultFilterBar: DefaultFilterBar };
};
/**
 * @alpha
 */
function DefaultFilterBar(props) {
    var filters = props.filters, onAttributeFilterChanged = props.onAttributeFilterChanged, onDateFilterChanged = props.onDateFilterChanged;
    var _a = tslib_1.__read(useFiltersWithAddedPlaceholder_1.useFiltersWithAddedPlaceholder(filters), 2), _b = _a[0], dateFilter = _b.dateFilter, attributeFiltersWithPlaceholder = _b.attributeFiltersWithPlaceholder, attributeFiltersCount = _b.attributeFiltersCount, autoOpenFilter = _b.autoOpenFilter, _c = _a[1], addAttributeFilterPlaceholder = _c.addAttributeFilterPlaceholder, closeAttributeSelection = _c.closeAttributeSelection, selectAttributeFilter = _c.selectAttributeFilter, onCloseAttributeFilter = _c.onCloseAttributeFilter;
    var isInEditMode = model_1.useDashboardSelector(model_1.selectIsInEditMode);
    var customFilterName = model_1.useDashboardSelector(model_1.selectEffectiveDateFilterTitle);
    var availableGranularities = model_1.useDashboardSelector(model_1.selectEffectiveDateFilterAvailableGranularities);
    var dateFilterOptions = model_1.useDashboardSelector(model_1.selectEffectiveDateFilterOptions);
    var dateFilterMode = model_1.useDashboardSelector(model_1.selectEffectiveDateFilterMode);
    var isExport = model_1.useDashboardSelector(model_1.selectIsExport);
    var _d = dashboardContexts_1.useDashboardComponentsContext(), AttributeFilterComponentSet = _d.AttributeFilterComponentSet, DashboardDateFilterComponentProvider = _d.DashboardDateFilterComponentProvider;
    var supportElementUris = model_1.useDashboardSelector(model_1.selectSupportsElementUris);
    var displayFormsMap = model_1.useDashboardSelector(model_1.selectAttributeFilterDisplayFormsMap);
    var canAddMoreAttributeFilters = model_1.useDashboardSelector(model_1.selectCanAddMoreAttributeFilters);
    if (isExport) {
        return react_1.default.createElement(HiddenFilterBar_1.HiddenFilterBar, tslib_1.__assign({}, props));
    }
    var dateFilterComponentConfig = {
        availableGranularities: availableGranularities,
        dateFilterOptions: dateFilterOptions,
        customFilterName: customFilterName,
    };
    var CustomDateFilterComponent = DashboardDateFilterComponentProvider(dateFilter);
    return (react_1.default.createElement(DefaultFilterBarContainer_1.DefaultFilterBarContainer, null,
        react_1.default.createElement("div", { className: classnames_1.default("dash-filters-date", {
                "dash-filter-is-edit-mode": isInEditMode,
            }) }, dateFilterMode === "hidden" ? (react_1.default.createElement(dateFilter_1.HiddenDashboardDateFilter, null)) : (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(CustomDateFilterComponent, { filter: dateFilter, onFilterChanged: onDateFilterChanged, config: dateFilterComponentConfig, readonly: dateFilterMode === "readonly" }),
            react_1.default.createElement(dragAndDrop_1.AttributeFilterDropZoneHint, { placement: "outside", hintPosition: "next", targetIndex: 0, onAddAttributePlaceholder: addAttributeFilterPlaceholder, acceptPlaceholder: canAddMoreAttributeFilters })))),
        attributeFiltersWithPlaceholder.map(function (filterOrPlaceholder) {
            if (useFiltersWithAddedPlaceholder_1.isFilterBarAttributeFilterPlaceholder(filterOrPlaceholder)) {
                var CreatingPlaceholderComponent = AttributeFilterComponentSet.creating.CreatingPlaceholderComponent;
                return (react_1.default.createElement(CreatingPlaceholderComponent, { key: filterOrPlaceholder.filterIndex, onClose: closeAttributeSelection, onSelect: selectAttributeFilter }));
            }
            else {
                var filter = filterOrPlaceholder.filter, filterIndex = filterOrPlaceholder.filterIndex;
                var convertedFilter = supportElementUris
                    ? filter
                    : legacyFilterConvertors_1.convertDashboardAttributeFilterElementsUrisToValues(filter);
                var CustomAttributeFilterComponent = AttributeFilterComponentSet.MainComponentProvider(convertedFilter);
                /**
                 * Use the attribute as key, not the display form.
                 * This is to make sure we do not remount this when user changes the display form used:
                 * it should just reload the elements, not close and remount the whole filter.
                 *
                 * This is fine because we do not allow multiple filters of the same attribute to be on
                 * the same dashboard.
                 */
                var displayForm = displayFormsMap.get(convertedFilter.attributeFilter.displayForm);
                ts_invariant_1.default(displayForm, "inconsistent state, display form for a filter was not found");
                return (react_1.default.createElement(dragAndDrop_1.DraggableAttributeFilter, { key: sdk_model_1.objRefToString(displayForm.attribute), autoOpen: sdk_model_1.areObjRefsEqual(filter.attributeFilter.displayForm, autoOpenFilter), filter: filter, filterIndex: filterIndex, FilterComponent: CustomAttributeFilterComponent, onAttributeFilterChanged: onAttributeFilterChanged, onAttributeFilterAdded: addAttributeFilterPlaceholder, onAttributeFilterClose: onCloseAttributeFilter }));
            }
        }),
        canAddMoreAttributeFilters ? (react_1.default.createElement(dragAndDrop_1.AttributeFilterDropZone, { targetIndex: attributeFiltersCount, onDrop: addAttributeFilterPlaceholder })) : null,
        react_1.default.createElement("div", { className: "filter-bar-dropzone-container" },
            react_1.default.createElement(dragAndDrop_1.AttributeFilterDropZoneHint, { placement: "outside", hintPosition: "prev", acceptPlaceholder: false, targetIndex: attributeFiltersCount }))));
}
exports.DefaultFilterBar = DefaultFilterBar;
//# sourceMappingURL=DefaultFilterBar.js.map